import { isEditForkingFinishedVersion } from "@/components/MedicalChronology/contexts/medicalChronologyVersionUtils";
import type { ChronologyVersion, RunVersionEdit } from "@/components/MedicalChronology/types";
import type { MedChronEditResult } from "@/services/medChronServiceV2.types";
import { isConflictError } from "@/utils/errorUtils";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";

interface UseChronologyEditGuardParams {
  caseStatusId: number | null;
  versions: ChronologyVersion[];
  fetchVersions: () => Promise<ChronologyVersion[]>;
  fetchVersionsAndSelectCurrent: () => Promise<void>;
}

interface ChronologyEditGuard {
  isCreatingVersion: boolean;
  runVersionEdit: RunVersionEdit;
}

export const useChronologyEditGuard = ({
  caseStatusId,
  versions,
  fetchVersions,
  fetchVersionsAndSelectCurrent,
}: UseChronologyEditGuardParams): ChronologyEditGuard => {
  const [pendingForkingEditCount, setPendingForkingEditCount] = useState(0);

  const switchToCurrentVersion = useCallback(async (): Promise<boolean> => {
    try {
      await fetchVersionsAndSelectCurrent();
      return true;
    } catch (error) {
      console.error("[useChronologyEditGuard] Failed to switch to the current version:", error);
      return false;
    }
  }, [fetchVersionsAndSelectCurrent]);

  const refreshVersions = useCallback(async () => {
    try {
      await fetchVersions();
    } catch {
      // fetchVersions already logs and toasts; the original conflict must still propagate.
    }
  }, [fetchVersions]);

  /**
   * Runs an edit against targetVersionId, then follows the version the API says now holds the chronology.
   * - Switches to the current version on a fork or any failure of an edit expected to fork.
   *   - The fork commits before the edit, so a failed edit can still leave a new current version.
   * - On any other 409, only refreshes the version list so the user stays on the version they were viewing.
   */
  const runVersionEdit = useCallback(
    async <EditResult extends MedChronEditResult>(
      targetVersionId: string,
      edit: () => Promise<EditResult>
    ): Promise<EditResult> => {
      const targetVersion = versions.find((v) => v.version_id === targetVersionId) ?? null;
      const isForkExpected = isEditForkingFinishedVersion(caseStatusId, targetVersion);
      if (isForkExpected) setPendingForkingEditCount((count) => count + 1);
      try {
        let result: EditResult;
        try {
          result = await edit();
        } catch (error) {
          if (isForkExpected) await switchToCurrentVersion();
          else if (isConflictError(error)) await refreshVersions();
          throw error;
        }
        if (result.forked && !(await switchToCurrentVersion())) {
          toast.error("Your edit is saved on a newer version. Reload to see it.");
        }
        return result;
      } finally {
        if (isForkExpected) setPendingForkingEditCount((count) => count - 1);
      }
    },
    [caseStatusId, refreshVersions, switchToCurrentVersion, versions]
  );

  return { isCreatingVersion: pendingForkingEditCount > 0, runVersionEdit };
};
